import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, Terminal } from 'lucide-react';
import { Button } from "@/components/common/ui/Button.tsx";
import { Input } from '@/components/common/ui/Input';
import { usePasswordReset } from '@/hooks/auth/usePasswordReset';
import { useResendCode } from '@/hooks/auth/useResendCode';
import { useAuth } from '@clerk/clerk-react';

const ResetPassword: React.FC = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        code: '',
        password: '',
        confirmPassword: ''
    });

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const { resetPassword, isLoading } = usePasswordReset();
    const { resendCode, isLoading: isResending } = useResendCode();
    const navigate = useNavigate();
    const { signOut } = useAuth(); // Hook to clear session post-reset

    // Get stored email for UI and resend functionality
    const email = localStorage.getItem('resetEmail');

    // Session check
    useEffect(() => {
        if (!email) {
            navigate('/auth/forgot-password');
        }
    }, [navigate, email]);

    // Focus first OTP input on mount
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const setInputRef = useCallback((index: number) => {
        return (el: HTMLInputElement | null) => {
            inputRefs.current[index] = el;
        };
    }, []);

    const handleOTPChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        setErrors(prev => ({ ...prev, code: '' }));

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleOTPPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (!/^\d+$/.test(pastedData)) return;

        const digits = pastedData.slice(0, 6).split('');
        const newOtp = [...otp];
        digits.forEach((digit, index) => {
            if (index < 6) newOtp[index] = digit;
        });
        setOtp(newOtp);

        const lastFilledIndex = Math.min(digits.length - 1, 5);
        inputRefs.current[lastFilledIndex]?.focus();
    };

    const validatePassword = (password: string) => {
        if (!password) return 'Password is required';
        if (password.length < 8) return 'Must be at least 8 characters';
        if (!/\d/.test(password)) return 'Must contain at least one number';
        if (!/[a-z]/.test(password)) return 'Must contain a lowercase letter';
        if (!/[A-Z]/.test(password)) return 'Must contain an uppercase letter';
        if (!/[!@#$%^&*]/.test(password)) return 'Must contain a special character';
        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));

        if (name === 'password') {
            const passwordError = validatePassword(value);
            setErrors(prev => ({
                ...prev,
                password: passwordError,
                confirmPassword: value !== formData.confirmPassword && formData.confirmPassword ? 'Passwords do not match' : ''
            }));
        }

        if (name === 'confirmPassword') {
            setErrors(prev => ({
                ...prev,
                confirmPassword: value !== formData.password ? 'Passwords do not match' : ''
            }));
        }
    };

    const handleResendCode = async () => {
        if (!email || isResending) return;

        await resendCode(email);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
    };

    const getPasswordStrength = (password: string) => {
        if (!password) return { width: '0%', color: 'bg-transparent' };

        const checks = [
            password.length >= 8,
            /\d/.test(password),
            /[a-z]/.test(password),
            /[A-Z]/.test(password),
            /[!@#$%^&*]/.test(password)
        ];

        const strength = checks.filter(Boolean).length;
        const percentage = (strength / checks.length) * 100;

        const colors = {
            1: 'bg-[#FF3333]',
            2: 'bg-[#ea580c]',
            3: 'bg-[#d97706]',
            4: 'bg-[#02DFC2]',
            5: 'bg-[#50A970]'
        };

        return {
            width: `${percentage}%`,
            color: colors[strength as keyof typeof colors] || 'bg-[#1A1D24]'
        };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isLoading) return;

        const code = otp.join('');
        if (!code || code.length !== 6) {
            setErrors(prev => ({ ...prev, code: 'Invalid 6-digit sequence' }));
            return;
        }

        const passwordError = validatePassword(formData.password);
        const confirmError = formData.password !== formData.confirmPassword
            ? 'Passwords do not match'
            : '';

        setErrors({
            code: '',
            password: passwordError,
            confirmPassword: confirmError
        });

        if (passwordError || confirmError) return;

        const response = await resetPassword(code, formData.password);
        if (response.success) {
            // 2. Terminate the auto-login session created by the provider post-reset 
            // so the user is forced to authenticate manually at /auth/login.
            await signOut();
            navigate('/auth/login');
        }
    };

    const passwordStrength = getPasswordStrength(formData.password);

    return (
        <div className="space-y-6 font-mono max-w-md mx-auto p-6 bg-white dark:bg-[#050505] border border-border-light dark:border-[#1A1D24] rounded-2xl shadow-lg dark:shadow-[0_0_50px_rgba(0,0,0,0.8)] relative overflow-hidden transition-all duration-300">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent shadow-[0_0_10px_#02DFC2]"></div>
            
            {/* Header / Identity Logo */}
            <div className="text-center space-y-3">
                <div className="flex justify-center mb-2">
                    <Link to="/" className="flex items-center z-50">
                        <img 
                            src="/image/logo.png" 
                            alt="ThreatInformant" 
                            className="h-20 w-52 object-contain"
                        />
                    </Link>
                </div>
                <h1 className="text-md font-bold text-text-primary-light dark:text-white tracking-[0.2em] uppercase flex items-center justify-center gap-2">
                    <Terminal className="h-4 w-4 text-[#02DFC2]" /> Reset Your Password
                </h1>
                <p className="text-[12px] text-text-secondary-light dark:text-slate-500 tracking-normal leading-relaxed">
                    Enter the verification code sent to your email and set your new password
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* OTP Input Grid */}
                <div className="space-y-4">
                    <label className="block text-[11px] font-bold text-text-primary-light dark:text-slate-400 uppercase tracking-widest">
                        VERIFICATION CODE
                    </label>
                    <div className="flex justify-between gap-2 max-w-xs mx-auto">
                        {otp.map((digit, index) => (
                            <div key={index} className="w-10 sm:w-12">
                                <input
                                    ref={setInputRef(index)}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    onChange={e => handleOTPChange(index, e.target.value)}
                                    onKeyDown={e => handleOTPKeyDown(index, e)}
                                    onPaste={handleOTPPaste}
                                    className="w-full h-12 text-center text-xl font-bold rounded-lg border border-border-light dark:border-[#1A1D24] bg-background-light dark:bg-black text-text-primary-light dark:text-[#02DFC2] focus:border-[#02DFC2] focus:outline-none transition-colors shadow-inner"
                                />
                            </div>
                        ))}
                    </div>
                    {errors.code && (
                        <p className="text-center text-[10px] text-red-600 dark:text-[#FF3333] uppercase tracking-widest">
                            {errors.code}
                        </p>
                    )}
                    <div className="text-center pt-2">
                        <button
                            type="button"
                            onClick={handleResendCode}
                            disabled={isResending}
                            className="text-[10px] text-slate-500 hover:text-[#02DFC2] transition-colors duration-200 uppercase tracking-widest disabled:opacity-50"
                        >
                            {isResending ? 'TRANSMITTING...' : "REQUEST NEW TOKEN"}
                        </button>
                    </div>
                </div>

                {/* New Password */}
                <div className="space-y-1">
                    <Input
                        label="NEW PASSWORD"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        className="bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-text-primary-light dark:text-[#02DFC2] h-11 text-[12px] placeholder-text-muted-light dark:placeholder:text-slate-800 focus:border-[#02DFC2]"
                        placeholder="Enter your new password..."
                        leftIcon={<Lock className="h-4 w-4 text-text-muted-light dark:text-slate-600" />}
                        rightIcon={
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="text-text-muted-light dark:text-slate-600 hover:text-text-primary-light dark:hover:text-[#02DFC2] transition-colors duration-200"
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        }
                        error={errors.password}
                    />

                    {/* Password strength indicator */}
                    {formData.password && (
                        <div className="h-1 w-full bg-[#1A1D24] rounded-full overflow-hidden mt-1">
                            <div
                                className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                                style={{ width: passwordStrength.width }}
                            />
                        </div>
                    )}
                </div>

                {/* Confirm Password */}
                <Input
                    label="CONFIRM PASSWORD"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password..."
                    className="bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-text-primary-light dark:text-[#02DFC2] h-11 text-[12px] placeholder-text-muted-light dark:placeholder:text-slate-800 focus:border-[#02DFC2]"
                    leftIcon={<Lock className="h-4 w-4 text-text-muted-light dark:text-slate-600" />}
                    rightIcon={
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="text-text-muted-light dark:text-slate-600 hover:text-text-primary-light dark:hover:text-[#02DFC2] transition-colors duration-200"
                        >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    }
                    error={errors.confirmPassword}
                />

                <Button
                    type="submit"
                    className="w-full bg-[#02DFC2] text-black font-black tracking-[0.2em] h-11 shadow-[0_0_15px_rgba(2,223,194,0.2)] hover:bg-[#00c9ae]"
                    size="lg"
                    loading={isLoading}
                    disabled={isLoading || !!errors.password || !!errors.confirmPassword || otp.join('').length !== 6}
                >
                    RESET PASSWORD
                </Button>
            </form>

            {/* Footer */}
            <div className="text-center pt-2">
                <Link
                    to="/auth/forgot-password"
                    className="inline-flex items-center space-x-2 text-[10px] text-slate-500 hover:text-[#02DFC2] transition-colors duration-200 uppercase tracking-widest"
                >
                    <ArrowLeft className="w-3 h-3" />
                    <span>Back to Forgot Password</span>
                </Link>
            </div>
        </div>
    );
};

export default ResetPassword;